import { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import API from '../../../../api/baseUrl';
import { useLocation } from 'react-router-dom';
import { MedProfile, UpdatePatientMedProfilePayload } from './types';
import { omit } from 'lodash';
import { getTokens } from '../../../../auth/AuthState';
import { useEnsurePayloadMedSectionOrder } from './useEnsurePayloadMedSectionOrder';

interface DeletePatientMedProfilesResponse {
  medProfiles: MedProfile[];
  access_token: string;
  tenancyId: number;
}

export const useDeleteMedProfiles = () => {
  const [errorMessage, setErrorMessage] = useState('');
  const [medProfiles, setMedProfiles] = useState<MedProfile[]>([]);
  const [isDeleting, setIsDeleting] = useState('');
  const { ensurePayloadMedSectionsOrder } = useEnsurePayloadMedSectionOrder();
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const access_token = searchParams.get('access_token');

  const postDeletePatientMedProfiles = async (medProfile: MedProfile) => {
    if (!selectedTenancy || !access_token) {
      const message = 'Missing tenancy or access token';
      setErrorMessage(message);
      return { success: false, message };
    }
    setIsDeleting(medProfile.id);
    try {
      const { id: authorization_token } = await getTokens();

      let options = {
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + authorization_token,
        },
      };

      const trimmedMedProfile = omit(medProfile, [
        'hospitalVerified',
        'healthProviderVerified',
      ]);
      const normalizedMedSections = ensurePayloadMedSectionsOrder(
        trimmedMedProfile?.medSections,
      );

      const deletePayload: UpdatePatientMedProfilePayload = {
        access_token: access_token,
        tenancyId: selectedTenancy.tenancy_id,
        medProfile: {
          id: trimmedMedProfile.id,
          profileStatus: 'entered in error',
          medSections: normalizedMedSections.map((section, index) => {
            const { sectionText: _sectionText, ...rest } = section as any;
            return {
              ...rest,
              order: Number((section as any)?.order) || index + 1,
              medications: (Array.isArray(section.medications)
                ? section.medications
                : []
              ).map((medication, medIndex) => ({
                ...omit(medication, [
                  'createdAt',
                  'deletedAt',
                  'updatedAt',
                  'hospitalVerified',
                  'healthProviderVerified',
                  'indicationOptions',
                  'indications',
                ]),
                order: Number(medication?.order) || medIndex + 1,
              })),
            };
          }),
        },
      };
      const { data: result } = await API.post(
        `updatePatientMedProfile`,
        { ...deletePayload },
        options,
      );
      const message: string = String(result?.message || '');
      if (message !== 'Success') {
        const failureMessage =
          message || 'Unknown error in updatePatientMedProfile';
        setErrorMessage(failureMessage);
        return { success: false, message: failureMessage };
      }
      const { data }: { data: DeletePatientMedProfilesResponse } = result;
      if (Array.isArray(data.medProfiles)) {
        setMedProfiles(data.medProfiles);
      }
      return { success: true };
    } catch (error: any) {
      console.error('An error occurred during updatePatientMedProfile', error);
      const message =
        error?.response?.data?.message ||
        'Unknown error in updatePatientMedProfile';
      setErrorMessage(message);
      return { success: false, message };
    } finally {
      setIsDeleting('');
    }
  };

  return {
    postDeletePatientMedProfiles,
    errorMessage,
    isDeleting,
    medProfiles,
  };
};
